import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage, pick } from "@/lib/lang";
import { sgb } from "@/lib/sgbContent";
import { IMG } from "@/lib/assets";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import Reveal from "@/components/ui-brand/Reveal";
import PeonyMark from "@/components/ui-brand/PeonyMark";
import SGBRibbon from "@/components/sgb/SGBRibbon";
import SGBReturnPill from "@/components/sgb/SGBReturnPill";
import WhatsAppButton from "@/components/sgb/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Eyebrow = ({ children, light, className = "" }) => (
  <p
    className={`font-mono text-[0.62rem] tracking-[0.32em] uppercase ${
      light ? "text-warmth/80" : "text-atmosphere"
    } ${className}`}
  >
    {children}
  </p>
);

const SGBHeader = () => {
  const { lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass =
    "font-mono text-[0.7rem] tracking-[0.32em] uppercase text-warmth/80 hover:text-canvas transition-colors";

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={[
        "fixed top-0 left-0 right-0 z-50 transition-all duration-700",
        scrolled
          ? "bg-depth/90 backdrop-blur-xl border-b border-warmth/15"
          : "bg-depth/70 backdrop-blur-md",
      ].join(" ")}
    >
      <div className="px-6 md:px-12 lg:px-16 py-5 md:py-6 flex items-center justify-between gap-6 text-canvas">
        <a
          href="/sgb"
          data-testid="sgb-brand-logo"
          className="flex items-baseline gap-3 select-none"
        >
          <span className="font-display text-xl md:text-[1.45rem] leading-none">
            Sichtbar <span className="italic-serif text-signal">·</span> Gut Bewertet
          </span>
          <span className="hidden md:inline font-mono text-[0.55rem] tracking-[0.32em] uppercase text-warmth/70">
            EST. 2026
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          <button type="button" onClick={() => scrollTo("bewertungen")} className={linkClass} data-testid="sgb-nav-reviews">
            {pick(sgb.nav.reviews, lang)}
          </button>
          <button type="button" onClick={() => scrollTo("clearsignal")} className={linkClass} data-testid="sgb-nav-clearsignal">
            {pick(sgb.nav.clearsignal, lang)}
          </button>
          <button type="button" onClick={() => scrollTo("webseiten")} className={linkClass} data-testid="sgb-nav-websites">
            {pick(sgb.nav.websites, lang)}
          </button>
          <button type="button" onClick={() => scrollTo("accounts")} className={linkClass} data-testid="sgb-nav-accounts">
            {pick(sgb.nav.accounts, lang)}
          </button>
          <button type="button" onClick={() => scrollTo("faq")} className={linkClass} data-testid="sgb-nav-faq">
            {pick(sgb.nav.faq, lang)}
          </button>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <button
            type="button"
            onClick={toggle}
            data-testid="sgb-lang-toggle"
            className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-warmth/80 hover:text-canvas transition-colors"
          >
            <span className={lang === "de" ? "text-canvas" : "text-warmth/50"}>DE</span>
            <span className="mx-2 text-warmth/40">/</span>
            <span className={lang === "en" ? "text-canvas" : "text-warmth/50"}>EN</span>
          </button>
          <button
            type="button"
            onClick={() => scrollTo("kontakt")}
            data-testid="sgb-nav-contact"
            className="font-mono text-[0.66rem] tracking-[0.32em] uppercase px-4 py-2 border border-warmth/40 hover:bg-canvas hover:text-depth transition-colors"
          >
            {pick(sgb.hero.cta_primary, lang)}
          </button>
        </div>
      </div>
    </header>
  );
};

const ContactForm = () => {
  const { lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        language: lang,
        source: "sichtbargutbewertet.de",
      });
      setStatus("success");
      setMsg(pick(sgb.contact.success, lang));
      setForm({ name: "", email: "", topic: "", message: "" });
    } catch (err) {
      setStatus("error");
      setMsg(pick(sgb.contact.error, lang));
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="sgb-contact-form"
      className="grid grid-cols-12 gap-x-8 gap-y-10"
    >
      <div className="col-span-12 md:col-span-6 field">
        <label className="field-label" htmlFor="sgb-c-name">{pick(sgb.contact.name, lang)}</label>
        <input
          id="sgb-c-name"
          type="text"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="sgb-contact-name"
          className="field-input"
          placeholder={lang === "de" ? "Dein Name" : "Your name"}
        />
      </div>
      <div className="col-span-12 md:col-span-6 field">
        <label className="field-label" htmlFor="sgb-c-email">{pick(sgb.contact.email, lang)}</label>
        <input
          id="sgb-c-email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="sgb-contact-email"
          className="field-input"
          placeholder="name@beispiel.de"
        />
      </div>
      <div className="col-span-12 field">
        <label className="field-label" htmlFor="sgb-c-topic">{pick(sgb.contact.topic_label, lang)}</label>
        <select
          id="sgb-c-topic"
          required
          value={form.topic}
          onChange={(e) => setForm({ ...form, topic: e.target.value })}
          data-testid="sgb-contact-topic"
          className="field-input bg-transparent appearance-none cursor-pointer"
        >
          <option value="">—</option>
          {sgb.contact.topic_options.map((opt, i) => (
            <option key={i} value={pick(opt, "de")}>{pick(opt, lang)}</option>
          ))}
        </select>
      </div>
      <div className="col-span-12 field">
        <label className="field-label" htmlFor="sgb-c-msg">{pick(sgb.contact.message, lang)}</label>
        <textarea
          id="sgb-c-msg"
          rows={5}
          required
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="sgb-contact-message"
          className="field-textarea"
          placeholder={lang === "de" ? "Worum geht es?" : "What is it about?"}
        />
      </div>
      <div className="col-span-12 flex flex-wrap items-center gap-4 pt-2">
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="sgb-contact-submit"
          className="btn-primary disabled:opacity-50"
        >
          {status === "sending" ? "…" : pick(sgb.contact.submit, lang)} <span aria-hidden>↗</span>
        </button>
        <WhatsAppButton />
        {status === "success" && (
          <p data-testid="sgb-contact-success" className="font-display italic text-atmosphere w-full md:w-auto">{msg}</p>
        )}
        {status === "error" && (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-signal w-full md:w-auto">{msg}</p>
        )}
      </div>
    </form>
  );
};

const SGBPage = () => {
  const { lang } = useLanguage();
  useDocumentMeta("sgb");
  const reviewsRef = useRef(null);
  const clearSignalRef = useRef(null);

  return (
    <div className="bg-canvas text-depth min-h-screen">
      <SGBHeader />

      {/* ============ HERO ============ */}
      <section
        data-testid="sgb-hero"
        className="relative bg-depth text-canvas overflow-hidden pt-32 md:pt-20 pb-24 md:pb-32"
      >
        <div className="sgb-ambient" aria-hidden="true" />
        <div className="absolute inset-0 grain pointer-events-none opacity-60" />
        <PeonyMark size={780} color="#FAF7F2" opacity={0.06} className="absolute -right-40 -top-32 pointer-events-none" />

        <div className="relative px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-6 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <Eyebrow light>— {pick(sgb.hero.eyebrow, lang)}</Eyebrow>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="headline-hero mt-8" style={{ color: "#fbfbfb" }}>
                {pick(sgb.hero.title_top, lang)}
                <br />
                <span className="italic-serif text-warmth">{pick(sgb.hero.title_bottom, lang)}</span>
              </h1>
            </Reveal>
            <Reveal delay={280}>
              <p className="mt-12 max-w-2xl font-mono text-[0.72rem] tracking-[0.32em] uppercase text-warmth/85">
                {pick(sgb.hero.subline, lang)}
              </p>
            </Reveal>
            <Reveal delay={420}>
              <div className="mt-12 flex flex-wrap items-center gap-5">
                <button
                  type="button"
                  onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="sgb-hero-cta"
                  className="btn-light"
                >
                  {pick(sgb.hero.cta_primary, lang)} <span aria-hidden>↗</span>
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById("bewertungen")?.scrollIntoView({ behavior: "smooth" })}
                  className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-warmth hover:text-canvas transition-colors flex items-center gap-2"
                >
                  {pick(sgb.hero.cta_ghost, lang)} ↓
                </button>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden bg-atmosphere/40 border border-warmth/20">
                <img
                  src={IMG.sgbAndroid}
                  alt="Sichtbar Gut Bewertet — Key Visual"
                  className="absolute inset-0 w-full h-full object-cover animate-slowpan"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-depth/55 via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ SERVICES OVERVIEW ============ */}
      <section className="relative px-6 md:px-12 lg:px-16 py-24 md:py-32 border-b border-warmth/40">
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {sgb.services_overview.map((s) => (
            <Reveal key={s.n} className="col-span-12 md:col-span-6 lg:col-span-3">
              <button
                type="button"
                onClick={() => document.getElementById({ reviews: "bewertungen", clearsignal: "clearsignal", accounts: "accounts", websites: "webseiten" }[s.key])?.scrollIntoView({ behavior: "smooth" })}
                className="text-left w-full border-l border-warmth pl-5 hover:border-signal transition-colors duration-500 group"
              >
                <Eyebrow>{s.n}</Eyebrow>
                <h3 className="font-display text-2xl md:text-3xl mt-3 text-depth">{pick(s.name, lang)}</h3>
                <p className="body-lux mt-5 text-sm">{pick(s.body, lang)}</p>
                <span className="mt-6 inline-block font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere group-hover:text-signal">
                  {lang === "de" ? "Mehr ansehen" : "Read more"} ↗
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ MANIFESTO ============ */}
      <section className="relative bg-soft_canvas px-6 md:px-12 lg:px-16 py-28 md:py-36 border-b border-warmth/40 overflow-hidden">
        <PeonyMark size={520} opacity={0.05} className="absolute -left-32 top-10 pointer-events-none" />
        <div className="relative grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <Eyebrow>— {pick(sgb.manifesto.eyebrow, lang)}</Eyebrow>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <ul className="space-y-6">
              {sgb.manifesto.body.map((line, i) => (
                <Reveal key={i} delay={i * 100}>
                  <li className="font-display italic text-2xl md:text-3xl lg:text-[2.4rem] leading-[1.18] text-depth">
                    {pick(line, lang)}
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={500}>
              <p className="body-lux mt-12 max-w-3xl">{pick(sgb.manifesto.closing, lang)}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ 01 · REVIEWS ============ */}
      <section
        id="bewertungen"
        ref={reviewsRef}
        data-ribbon-scope="reviews"
        data-testid="sgb-section-reviews"
        className="relative px-6 md:px-12 lg:px-16 py-28 md:py-36 border-b border-warmth/40"
      >
        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <Eyebrow>{pick(sgb.reviews.eyebrow, lang)}</Eyebrow>
              <h2 className="headline-display mt-6 max-w-2xl">
                <span className="italic-serif text-atmosphere">{pick(sgb.reviews.title, lang).split(" ").slice(0, 2).join(" ")}</span>{" "}
                {pick(sgb.reviews.title, lang).split(" ").slice(2).join(" ")}
              </h2>
              <p className="body-lux-lg mt-10 max-w-2xl">{pick(sgb.reviews.body, lang)}</p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden bg-warmth/30">
                <img
                  src={IMG.sgbReputation}
                  alt={pick(sgb.reviews.title, lang)}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-depth/40 via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>

        {/* Process */}
        <div className="mt-24 md:mt-32">
          <Reveal>
            <Eyebrow>— {pick(sgb.reviews.process_title, lang)}</Eyebrow>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {sgb.reviews.steps.map((step) => (
              <Reveal key={step.n}>
                <div className="border-t-2 border-signal pt-5">
                  <Eyebrow>{step.n}</Eyebrow>
                  <h4 className="font-display italic text-2xl mt-3 text-depth">{pick(step.title, lang)}</h4>
                  <p className="body-lux mt-4 text-sm">{pick(step.body, lang)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Prices */}
        <div className="mt-24 md:mt-32">
          <Reveal>
            <h3 className="headline-display">
              <span className="italic-serif">{pick(sgb.reviews.prices_title, lang).split("·")[0].trim()}</span>{" "}
              <span className="text-atmosphere">· {pick(sgb.reviews.prices_title, lang).split("·")[1]?.trim()}</span>
            </h3>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {sgb.reviews.prices.map((p, i) => (
              <Reveal key={i} delay={i * 100}>
                <article
                  className={`relative p-8 md:p-10 h-full ${
                    p.tag ? "bg-depth text-canvas" : "bg-clarity text-depth border border-warmth/40"
                  }`}
                >
                  {p.tag && (
                    <span className="absolute top-4 right-4 font-mono text-[0.55rem] tracking-[0.32em] uppercase text-signal border border-signal/60 px-2 py-0.5">
                      {pick(p.tag, lang)}
                    </span>
                  )}
                  <p className={`font-display text-4xl md:text-5xl leading-none ${p.tag ? "text-canvas" : "text-depth"}`}>
                    {typeof p.price === "string" ? p.price : pick(p.price, lang)}
                  </p>
                  <p className={`mt-3 font-mono text-[0.62rem] tracking-[0.28em] uppercase ${p.tag ? "text-warmth" : "text-atmosphere"}`} style={{ minHeight: "1em" }}>
                    {p.suffix ? pick(p.suffix, lang) : "\u00A0"}
                  </p>
                  <h4 className={`mt-8 font-display italic text-2xl ${p.tag ? "text-canvas" : "text-depth"}`}>
                    {pick(p.name, lang)}
                  </h4>
                  <p className={`mt-4 text-sm font-light leading-relaxed ${p.tag ? "text-warmth/90" : "text-atmosphere"}`}>
                    {pick(p.body, lang)}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-12">
              <button
                type="button"
                onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-primary"
              >
                {pick(sgb.reviews.cta, lang)} <span aria-hidden>↗</span>
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ 02 · CLEAR SIGNAL™ ============ */}
      <section
        id="clearsignal"
        ref={clearSignalRef}
        data-ribbon-scope="clearsignal"
        data-testid="sgb-section-clearsignal"
        className="relative bg-depth text-canvas px-6 md:px-12 lg:px-16 py-28 md:py-36 overflow-hidden border-b border-warmth/15"
      >
        <div className="absolute inset-0 grain pointer-events-none opacity-50" />
        <PeonyMark size={620} color="#FAF7F2" opacity={0.05} className="absolute -left-40 -bottom-32 pointer-events-none" />

        <div className="relative grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <Eyebrow light>{pick(sgb.clearsignal.eyebrow, lang)}</Eyebrow>
              <h2 className="headline-display mt-8 text-canvas max-w-3xl">
                <span className="italic-serif text-warmth">
                  {pick(sgb.clearsignal.title, lang).split(".")[0]}.
                </span>{" "}
                {pick(sgb.clearsignal.title, lang).split(".").slice(1).join(".")}
              </h2>
              <p className="mt-10 max-w-2xl font-light text-warmth/90 text-lg leading-relaxed">
                {pick(sgb.clearsignal.body, lang)}
              </p>
              <p className="mt-6 font-mono text-[0.7rem] tracking-[0.32em] uppercase text-warmth/85">
                {pick(sgb.clearsignal.sub, lang)}
              </p>
            </Reveal>
          </div>
        </div>

        {/* ============ CLEAR SIGNAL Image Slot (Frau auf Würfel · Instagram) ============ */}
        <div className="relative mt-20 grid grid-cols-12 gap-6 lg:gap-12 items-center">
          <Reveal className="col-span-12 lg:col-span-6">
            <div className="relative overflow-hidden bg-warmth/10 border border-warmth/20">
              <img
                src={IMG.clearSignalHero}
                alt={pick(sgb.clearsignal.title, lang)}
                className="block w-full h-auto object-cover"
              />
              <div className="absolute top-4 left-4 font-mono text-[0.55rem] tracking-[0.32em] uppercase text-canvas/90 mix-blend-difference">
                N° 002 · CLEAR SIGNAL™
              </div>
            </div>
          </Reveal>
          <Reveal delay={140} className="col-span-12 lg:col-span-6">
            <p className="font-display italic text-warmth/95 text-2xl md:text-3xl leading-snug max-w-xl">
              {lang === "de"
                ? "Dein Profil ist mehr als ein Feed. Es ist die digitale Tür, durch die Menschen entscheiden, ob sie eintreten — oder weiterscrollen."
                : "Your profile is more than a feed. It is the digital door through which people decide to step in — or scroll past."}
            </p>
          </Reveal>
        </div>

        {/* ============ CLEAR SIGNAL · Editorial 2-Spalten Akronym-Grid ============ */}
        <div
          data-testid="sgb-clearsignal-acronym"
          className="relative mt-28 md:mt-36"
        >
          <Reveal>
            <Eyebrow light>{pick(sgb.clearsignal.acronym.eyebrow, lang)}</Eyebrow>
            <h3 className="headline-display mt-8 max-w-3xl" style={{ color: "#FAF7F2" }}>
              {pick(sgb.clearsignal.benefits.title, lang)}
            </h3>
          </Reveal>

          <div className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-16 gap-y-2 max-w-5xl">
            {[
              { col: sgb.clearsignal.acronym.clear,  offset: 0, label: "CLEAR"  },
              { col: sgb.clearsignal.acronym.signal, offset: 5, label: "SIGNAL" },
            ].map(({ col, offset, label }) => (
              <div key={label}>
                <p
                  className="font-mono text-[0.62rem] tracking-[0.42em] uppercase mb-6"
                  style={{ color: "#FF7100" }}
                >
                  — {label}
                </p>
                <ul role="list" className="space-y-5">
                  {col.map((row, i) => {
                    const word = pick(row.word, lang);
                    const first = word.charAt(0);
                    const rest = word.slice(1);
                    const desc = pick(sgb.clearsignal.benefits.items[offset + i].right, lang);
                    return (
                      <Reveal key={`${label}-${i}`} delay={i * 40}>
                        <li>
                          <div className="flex items-baseline" style={{ lineHeight: 1 }}>
                            <span
                              className="font-display"
                              style={{
                                color: "#FF7100",
                                fontSize: "clamp(1.9rem, 2.6vw, 2.3rem)",
                                fontWeight: 600,
                                letterSpacing: "-0.01em",
                              }}
                            >
                              {first}
                            </span>
                            <span
                              className="font-display"
                              style={{
                                color: "#FAF7F2",
                                fontSize: "clamp(1.1rem, 1.4vw, 1.25rem)",
                                fontWeight: 400,
                                letterSpacing: "0.14em",
                                marginLeft: "0.05em",
                              }}
                            >
                              {rest}
                            </span>
                          </div>
                          <p
                            className="font-light mt-1 leading-snug"
                            style={{
                              color: "rgba(250, 247, 242, 0.72)",
                              fontSize: "clamp(0.82rem, 0.95vw, 0.92rem)",
                              paddingLeft: "1.6em",
                              fontStyle: "italic",
                              fontFamily: "'Cormorant Garamond', serif",
                            }}
                          >
                            {desc}
                          </p>
                        </li>
                      </Reveal>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <Reveal>
            <Eyebrow light>— {pick(sgb.clearsignal.packages_title, lang)}</Eyebrow>
            <p className="mt-4 font-mono text-[0.66rem] tracking-[0.32em] uppercase text-warmth/70">
              {pick(sgb.clearsignal.packages_available, lang)}
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {sgb.clearsignal.packages.map((p, i) => {
              const isFocus = p.name === "Pro";
              const bg = isFocus ? "#28001B" : "#FDFDFD";
              const fg = isFocus ? "#FAF7F2" : "#28001B";
              const sub = isFocus ? "rgba(250,247,242,0.85)" : "#5C4752";
              const accent = "#FF7100";
              const borderColor = isFocus ? "#FF7100" : "rgba(40,0,27,0.18)";
              return (
                <Reveal key={p.name} delay={i * 100}>
                  <article
                    className="relative p-6 md:p-7 h-full border"
                    style={{ background: bg, color: fg, borderColor }}
                  >
                    {p.tag && (
                      <span
                        className="absolute top-3 right-3 font-mono text-[0.55rem] tracking-[0.32em] uppercase px-2 py-0.5 border"
                        style={{ color: accent, borderColor: accent }}
                      >
                        {pick(p.tag, lang)}
                      </span>
                    )}
                    <p
                      className="font-mono text-[0.66rem] tracking-[0.32em] uppercase"
                      style={{ color: isFocus ? accent : "#28001B" }}
                    >
                      {p.name}
                    </p>
                    <p
                      className="font-display text-4xl md:text-5xl leading-none mt-5"
                      style={{ color: fg }}
                    >
                      {p.price}
                    </p>
                    <p
                      className="mt-5 text-sm font-light leading-relaxed"
                      style={{ color: sub }}
                    >
                      {pick(p.body, lang)}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={280}>
            <div className="mt-12 border-l-2 border-signal/60 pl-6 max-w-2xl">
              <p className="font-display italic text-warmth/95 text-xl md:text-2xl leading-snug">
                {pick(sgb.clearsignal.note, lang)}
              </p>
              <button
                type="button"
                onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="sgb-clearsignal-to-contact"
                className="mt-5 group inline-flex items-center gap-3 font-mono text-[0.66rem] tracking-[0.32em] uppercase text-canvas border border-warmth/40 hover:border-signal/70 px-5 py-3 transition-all"
              >
                {lang === "de" ? "Zur Anfrage" : "Get in touch"}
                <span aria-hidden style={{ color: "#FF7100" }} className="group-hover:translate-x-0.5 transition-transform">↘</span>
              </button>
            </div>
          </Reveal>
        </div>

        {/* ============ Instagram · Folge dem Signal — schicker als Card-Continuation ============ */}
        <div
          data-testid="sgb-instagram-block"
          className="relative mt-12 md:mt-16"
        >
          <div
            className="relative max-w-3xl p-7 md:p-10 overflow-hidden"
            style={{
              background: "rgba(82, 51, 69, 0.35)",
              border: "1px solid rgba(255, 113, 0, 0.25)",
              backdropFilter: "blur(6px)",
            }}
          >
            {/* Decorative IG glyph */}
            <span
              aria-hidden
              className="absolute -top-6 -right-6 opacity-15 pointer-events-none"
              style={{ fontSize: "10rem", lineHeight: 1, color: "#FF7100" }}
            >
              ✦
            </span>

            <Reveal>
              <Eyebrow light>{pick(sgb.clearsignal.instagram.eyebrow, lang)}</Eyebrow>
            </Reveal>
            <Reveal delay={80}>
              <a
                href={sgb.clearsignal.instagram.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sgb-instagram-cta"
                className="group block mt-4"
              >
                <p
                  className="font-display italic-serif tracking-tight leading-none transition-all group-hover:translate-x-1"
                  style={{
                    color: "#FF7100",
                    fontSize: "clamp(1.7rem, 3vw, 2.4rem)",
                    fontFamily: "'Cormorant Garamond', serif",
                  }}
                >
                  {sgb.clearsignal.instagram.handle}
                </p>
              </a>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-4 max-w-xl font-light text-warmth/90 text-base">
                {pick(sgb.clearsignal.instagram.tagline, lang)}
              </p>
            </Reveal>
            <Reveal delay={200}>
              <a
                href={sgb.clearsignal.instagram.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth hover:text-canvas transition-colors"
              >
                {pick(sgb.clearsignal.instagram.cta, lang)} <span aria-hidden>↗</span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ 03 · WEBSITES ============ */}
      <section
        id="webseiten"
        data-testid="sgb-section-websites"
        className="relative bg-soft_canvas px-6 md:px-12 lg:px-16 py-28 md:py-36 border-b border-warmth/40 overflow-hidden"
      >
        <PeonyMark size={620} opacity={0.04} className="absolute -right-32 -top-20 pointer-events-none" />
        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <Eyebrow>{pick(sgb.websites.eyebrow, lang)}</Eyebrow>
              <h2 className="headline-display mt-6 max-w-3xl">
                {pick(sgb.websites.title_top, lang)}
                <br />
                <span className="italic-serif text-atmosphere">{pick(sgb.websites.title_bottom, lang)}</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative aspect-[4/5] overflow-hidden bg-warmth/30">
                <img src={IMG.sgbWebdesign} alt="Webseiten" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
          {sgb.websites.audiences.map((a) => (
            <Reveal key={a.n}>
              <div className="border-l border-warmth pl-5">
                <Eyebrow>{a.n}</Eyebrow>
                <h4 className="font-display text-2xl mt-3 text-depth">{pick(a.name, lang)}</h4>
                <p className="mt-3 text-sm font-light text-atmosphere">{pick(a.body, lang)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {sgb.websites.types.map((tp, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="bg-clarity p-8 md:p-10 border border-warmth/40 hover:border-depth transition-colors duration-500 h-full">
                <h4 className="font-display italic text-3xl text-depth">{pick(tp.name, lang)}</h4>
                <p className="body-lux mt-6">{pick(tp.body, lang)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-24">
          <Reveal>
            <h3 className="headline-display">
              <span className="italic-serif text-atmosphere">{pick(sgb.websites.process_title, lang)}</span>
            </h3>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-10">
            {sgb.websites.steps.map((step) => (
              <Reveal key={step.n}>
                <div className="border-t-2 border-depth pt-5">
                  <Eyebrow>{step.n}</Eyebrow>
                  <h4 className="font-display italic text-2xl mt-3 text-depth">{pick(step.title, lang)}</h4>
                  <p className="mt-4 text-sm font-light text-atmosphere">{pick(step.body, lang)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <button
              type="button"
              onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-primary mt-12"
            >
              {pick(sgb.websites.cta, lang)} <span aria-hidden>↗</span>
            </button>
          </Reveal>
        </div>
      </section>

      {/* ============ 04 · ACCOUNTS ============ */}
      <section
        id="accounts"
        data-testid="sgb-section-accounts"
        className="relative px-6 md:px-12 lg:px-16 py-28 md:py-36 border-b border-warmth/40"
      >
        <div className="grid grid-cols-12 gap-6 lg:gap-12 lg:grid-rows-[auto_1fr]">
          <div className="col-span-12 lg:col-span-7 lg:col-start-1 lg:row-start-1">
            <Reveal>
              <Eyebrow>{pick(sgb.accounts.eyebrow, lang)}</Eyebrow>
              <h2 className="headline-display mt-6 max-w-2xl">
                <span className="italic-serif text-atmosphere">{pick(sgb.accounts.title, lang).split("?")[0]}?</span>
                {pick(sgb.accounts.title, lang).split("?").slice(1).join("?") && (
                  <>
                    <br />
                    {pick(sgb.accounts.title, lang).split("?").slice(1).join("?")}
                  </>
                )}
              </h2>
            </Reveal>
          </div>
          {/* Image — mobile: between headline and body. Desktop: right column, spans both rows. */}
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2">
            <Reveal delay={120}>
              <div className="relative aspect-square overflow-hidden bg-canvas">
                <img
                  src="https://customer-assets.emergentagent.com/job_peony-redesign/artifacts/4o8mlmx8_Foto%2025-05-2026%2C%2015%2035%2057%20%282%29.jpg"
                  alt="Account gesperrt — das muss nicht das Ende sein"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-1 lg:row-start-2">
            <Reveal delay={60}>
              <p className="font-display italic text-2xl md:text-3xl text-depth max-w-2xl">
                {pick(sgb.accounts.body, lang)}
              </p>
              <p className="body-lux mt-8 max-w-2xl">{pick(sgb.accounts.body2, lang)}</p>
              <button
                type="button"
                onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-primary mt-12"
              >
                {pick(sgb.accounts.cta, lang)} <span aria-hidden>↗</span>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section
        id="faq"
        data-testid="sgb-section-faq"
        className="relative px-6 md:px-12 lg:px-16 py-28 md:py-36 border-b border-warmth/40"
      >
        <Reveal>
          <h2 className="headline-display max-w-3xl">
            <span className="italic-serif text-atmosphere">
              {pick(sgb.faq.title, lang).split(" ")[0]}
            </span>{" "}
            {pick(sgb.faq.title, lang).split(" ").slice(1).join(" ")}
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-12 gap-6 lg:gap-12">
          {sgb.faq.items.map((item, i) => (
            <Reveal key={i} delay={i * 80} className="col-span-12 md:col-span-6">
              <details
                className="group border-t border-warmth/40 py-6 cursor-pointer"
                data-testid={`sgb-faq-${i}`}
              >
                <summary className="list-none flex items-start justify-between gap-6">
                  <h3 className="font-display text-2xl md:text-3xl text-depth leading-tight">
                    {pick(item.q, lang)}
                  </h3>
                  <span className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-atmosphere shrink-0 mt-2 transition-transform duration-500 group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="body-lux mt-6 max-w-xl">{pick(item.a, lang)}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ DISCLAIMER + PRICING NOTE ============ */}
      <section className="relative bg-warmth/15 px-6 md:px-12 lg:px-16 py-20 md:py-24 border-b border-warmth/40">
        <Reveal>
          <Eyebrow>— {pick(sgb.disclaimer.label, lang)}</Eyebrow>

          {/* Elegantes Editorial-Dropdown für den Dienstleistungs-Hinweis */}
          <details
            data-testid="sgb-disclaimer-dropdown"
            className="mt-8 max-w-4xl group border-t border-b border-atmosphere/25 hover:border-atmosphere/50 transition-colors"
          >
            <summary
              className="flex items-baseline justify-between gap-6 py-5 cursor-pointer list-none select-none"
              style={{ WebkitTapHighlightColor: "transparent" }}
            >
              <span
                className="font-display italic text-atmosphere text-lg md:text-xl leading-snug"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {pick(sgb.disclaimer.summary, lang)}
              </span>
              <span
                aria-hidden
                className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal flex-shrink-0 transition-transform duration-500 group-open:rotate-45 origin-center"
                style={{ display: "inline-block" }}
              >
                +
              </span>
            </summary>
            <p
              className="pb-7 pr-8 text-sm md:text-[0.95rem] font-light leading-relaxed text-atmosphere"
              style={{
                animation: "disclaimerReveal 600ms cubic-bezier(.2,.7,.2,1) both",
              }}
            >
              {pick(sgb.disclaimer.body, lang)}
            </p>
          </details>

          {/* Preise & Kleinunternehmer-Hinweis nach § 19 UStG */}
          <div className="mt-12 max-w-4xl">
            <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-signal mb-4">
              — {pick(sgb.pricingNote.label, lang)}
            </p>
            <p className="text-sm md:text-[0.95rem] font-light leading-relaxed text-atmosphere">
              {pick(sgb.pricingNote.payment, lang)}
            </p>
            <p
              className="mt-4 text-xs md:text-sm font-light leading-relaxed text-atmosphere/80 italic-serif"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
            >
              {pick(sgb.pricingNote.vat, lang)}
            </p>
          </div>
        </Reveal>
      </section>

      {/* ============ CONTACT ============ */}
      <section
        id="kontakt"
        data-testid="sgb-section-contact"
        className="relative px-6 md:px-12 lg:px-16 py-28 md:py-36"
      >
        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <Eyebrow>{pick(sgb.contact.eyebrow, lang)}</Eyebrow>
              <h2 className="headline-display mt-6">
                <span className="italic-serif text-atmosphere">{pick(sgb.contact.title, lang).split(" ")[0]}</span>{" "}
                {pick(sgb.contact.title, lang).split(" ").slice(1).join(" ")}
              </h2>
              <p className="body-lux-lg mt-10 max-w-md">{pick(sgb.contact.lede, lang)}</p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={140}>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative bg-depth text-canvas px-6 md:px-12 lg:px-16 pt-20 pb-10 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <PeonyMark size={620} color="#FAF7F2" opacity={0.06} className="absolute -right-32 -bottom-20 pointer-events-none" />
        <div className="relative grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <p className="font-display text-3xl leading-tight">
              Sichtbar <span className="italic-serif text-signal">·</span> Gut Bewertet
            </p>
            <p className="mt-4 max-w-md font-light text-warmth/85 text-sm">
              {pick(sgb.hero.subline, lang)}
            </p>
            <p className="mt-8 font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth/70">
              {pick(sgb.footer.line, lang)}
            </p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <ul className="grid grid-cols-2 gap-y-4 text-sm font-light">
              {sgb.footer.legal.map((l, i) => (
                <li key={i}>
                  <Link to={l.href} className="hover:text-warmth transition-colors">
                    {pick(l.name, lang)} →
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="relative mt-16 pt-8 border-t border-warmth/20 flex flex-col md:flex-row justify-between gap-4">
          <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth/70">
            {pick(sgb.footer.copyright, lang)}
          </p>
          <a
            href="https://patrice.studio"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth/70 hover:text-canvas transition-colors"
          >
            ← patrice.studio ↗
          </a>
        </div>
      </footer>

      <SGBRibbon sectionsMap={{ reviews: reviewsRef, clearsignal: clearSignalRef }} />
      <SGBReturnPill />
    </div>
  );
};

export default SGBPage;
